import { CircularProgress } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl, FramerTooltip } from "@ryde/controls";
import { IRideDto, TOOLTIP_DELAY_BUTTONS } from "@ryde/shared";

import { ButtonHeader } from "../../../../right";
import { useRideMeasuredTimes } from "./use-ride-measured-times";

/** Matches the 20px `ButtonHeader` sizes its icons to, so the swap does not resize the header. */
const MEASURED_TIMES_SPINNER_SIZE = 20;

interface IRideMeasuredTimesButtonProps {
  rideDto: Partial<IRideDto>;
}

export const RideMeasuredTimesButton: FC<IRideMeasuredTimesButtonProps> = ({
  rideDto,
}) => {
  const { t } = useTranslation();
  const { isFlowOpen, openMeasuredTimes } = useRideMeasuredTimes(rideDto);

  const handleButtonClick = useEvent(() => {
    void openMeasuredTimes();
  });

  return (
    <AccessControl entity="ride" action="update">
      <FramerTooltip
        content={t("stationTiming.MEASURED_TIMES_TOOLTIP")}
        mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
      >
        {/* Icon only (RYDE-8064) — the tooltip and the accessible name carry the wording the header used
            to spell out. Disabled while the flow is open: what the ticket objected to was sitting on a
            dead button for 4-5 seconds, and that is gone now that the dialog opens on the click, so the
            only stretch left is the few frames before it paints. The spinner says why. */}
        <ButtonHeader
          onClick={handleButtonClick}
          disabled={isFlowOpen}
          aria-label={t("stationTiming.MEASURED_TIMES")}
          aria-busy={isFlowOpen}
        >
          {isFlowOpen ? (
            <CircularProgress
              size={MEASURED_TIMES_SPINNER_SIZE}
              data-e2e="ride-measured-times-spinner"
            />
          ) : (
            <SVG_MUI.ChartBarSVG data-e2e="ride-measured-times-svg" />
          )}
        </ButtonHeader>
      </FramerTooltip>
    </AccessControl>
  );
};
